import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { EGroupActions, trackGroupActionClick } from './tracking';
import { openChatActionModal } from '../../actions/chatActionModal';
import {
  markAllChatsAsRead,
  markChatsAsRead,
  archiveAllChats,
  archiveChats,
  restoreAllChatsFromArchive,
  restoreChatsFromArchive,
} from '../../actions/chatList';
import { ESection as EArchiveSection } from '../../repositories/chats/v1/chats-bulk-operations-move-all-from-section-to-archive-desktop';
import { ESection as EReadSection } from '../../repositories/chats/v1/chats-bulk-operations-read-all-from-section-desktop';
import { chatListSelector } from '../../selectors';
import { ERequestApiStatus } from '../../types/appState';
import { EChatAction } from '../../types/chatActionModal';
import { ERoute } from '../../types/route';

type TRoutesAvailableToChangeForRead = Exclude<ERoute, ERoute.Spam>;
type TRoutesAvailableToChangeForArchive = Exclude<ERoute, ERoute.Spam | ERoute.Archive>;

const RouteToReadSectionMap = {
  [ERoute.Messages]: EReadSection.Main,
  [ERoute.Archive]: EReadSection.Archive,
  [ERoute.Unread]: EReadSection.Unread,
};

const RouteToArchiveSectionMap = {
  [ERoute.Messages]: EArchiveSection.Main,
  [ERoute.Unread]: EArchiveSection.Unread,
};

export const useListActionButtons = () => {
  const dispatch = useDispatch();

  const { isChangeListActive, selectedChats, currentRoute, chats, status } = useSelector(chatListSelector);

  const isReadButtonDisabled = React.useMemo(() => {
    const unreadChats = chats.filter(({ unread }) => unread);

    // если нет непрочитанных чатов, то читать нечего
    if (!unreadChats.length) {
      return true;
    }

    // если выбраны чаты и среди них нет непрочитанных, то тоже читать нечего
    return selectedChats.length > 0 && !unreadChats.some(({ chatId }) => selectedChats.includes(chatId));
  }, [selectedChats, chats]);

  const onArchiveClick = React.useCallback(() => {
    if (selectedChats.length) {
      dispatch(archiveChats(selectedChats));
      trackGroupActionClick(
        EGroupActions.Archive,
        chats.filter(({ chatId }) => selectedChats.includes(chatId)),
      );

      return;
    }

    dispatch(archiveAllChats(RouteToArchiveSectionMap[currentRoute as TRoutesAvailableToChangeForArchive]));
    trackGroupActionClick(EGroupActions.Archive);
  }, [dispatch, selectedChats, currentRoute, chats]);

  const onDeleteClick = React.useCallback(
    () =>
      dispatch(
        openChatActionModal({
          buttonText: 'Удалить',
          text: `Точно хотите удалить чат${selectedChats.length === 1 ? '' : 'ы'}?`,
          chatAction: EChatAction.GroupDelete,
        }),
      ),
    [dispatch, selectedChats],
  );

  const onReadClick = React.useCallback(() => {
    if (selectedChats.length) {
      dispatch(markChatsAsRead(selectedChats));
      trackGroupActionClick(
        EGroupActions.Read,
        chats.filter(({ chatId }) => selectedChats.includes(chatId)),
      );

      return;
    }

    dispatch(markAllChatsAsRead(RouteToReadSectionMap[currentRoute as TRoutesAvailableToChangeForRead]));
    trackGroupActionClick(EGroupActions.Read);
  }, [dispatch, currentRoute, selectedChats, chats]);

  const onRestoreClick = React.useCallback(() => {
    if (selectedChats.length) {
      dispatch(restoreChatsFromArchive(selectedChats));
      trackGroupActionClick(
        EGroupActions.Unarchive,
        chats.filter(({ chatId }) => selectedChats.includes(chatId)),
      );

      return;
    }

    dispatch(restoreAllChatsFromArchive());
    trackGroupActionClick(EGroupActions.Unarchive);
  }, [dispatch, selectedChats, chats]);

  return React.useMemo(
    () => ({
      isChangeListActive,
      isReadButtonDisabled,
      onArchiveClick,
      onDeleteClick,
      onReadClick,
      onRestoreClick,
      isMultiple: !selectedChats.length,
      isPending: status === ERequestApiStatus.Pending,
      isArchive: currentRoute === ERoute.Archive,
    }),
    [
      isChangeListActive,
      isReadButtonDisabled,
      onArchiveClick,
      onDeleteClick,
      onReadClick,
      onRestoreClick,
      selectedChats,
      status,
      currentRoute,
    ],
  );
};
